import {Injectable} from '@angular/core';
import {
  ContainerAdvancedSearchMatch,
  ContainerAdvancedSearchRow,
  ContainerAdvancedSearchSection,
  ContainerAdvancedSearchSummary
} from '../models/container-advanced-search';

const BULK_SPLIT_REGEX = /[\r\n,;\t ]+/;
const CONTAINER_FORMAT_REGEX = /^[A-Z0-9]+$/;

@Injectable({
  providedIn: 'root'
})
export class ContainerAdvancedSearchService {

  parseAndNormalize(input: string): string[] {
    if (!input?.trim()) {
      return [];
    }

    const unique = new Set<string>();
    const tokens = input
      .split(BULK_SPLIT_REGEX)
      .map(token => token.trim().toUpperCase())
      .filter(Boolean);

    tokens.forEach(token => unique.add(token));
    return [...unique];
  }

  isValidIso6346(containerNumber: string): boolean {
    return CONTAINER_FORMAT_REGEX.test(containerNumber);
  }

  buildRows(
    normalizedContainers: string[],
    matches: ContainerAdvancedSearchMatch[],
  ): ContainerAdvancedSearchRow[] {
    const matchMap = new Map<string, ContainerAdvancedSearchMatch[]>();
    matches.forEach(match => {
      const normalized = match.containerNumber.trim().toUpperCase();
      if (!normalized) {
        return;
      }
      const currentMatches = matchMap.get(normalized) || [];
      currentMatches.push({
        ...match,
        containerNumber: normalized,
      });
      matchMap.set(normalized, currentMatches);
    });

    const rows: ContainerAdvancedSearchRow[] = [];
    normalizedContainers.forEach(containerNumber => {
      const matchList = matchMap.get(containerNumber) || [];
      if (matchList.length > 0) {
        matchList.forEach(match => {
          rows.push({
            containerNumber,
            classification: 'found',
            currentStatus: match.currentStatus || '-',
            currentStatusClass: match.currentStatusClass || 'subtle-grey',
            currentStatusIcon: match.currentStatusIcon || '',
            lastEventDate: match.lastEventDate || '-',
            referenceOrder: match.referenceOrder || '-',
            containerTypeCode: match.containerTypeCode || '-',
          });
        });
        return;
      }

      if (!this.isValidIso6346(containerNumber)) {
      rows.push({
        containerNumber,
        classification: 'invalid',
        currentStatus: '-',
        lastEventDate: '-',
        referenceOrder: '-',
        containerTypeCode: '-',
      });
        return;
      }

      rows.push({
        containerNumber,
        classification: 'not_found',
        currentStatus: '-',
        lastEventDate: '-',
        referenceOrder: '-',
        containerTypeCode: '-',
      });
    });

    const unresolvedRows = rows.filter(row => row.classification !== 'found');
    const foundRows = rows.filter(row => row.classification === 'found');
    return [...unresolvedRows, ...foundRows];
  }

  buildSummary(
    normalizedContainers: string[] | ContainerAdvancedSearchRow[],
    matches: ContainerAdvancedSearchMatch[] = []
  ): ContainerAdvancedSearchSummary {
    if (normalizedContainers.length === 0) {
      return {
        total: 0,
        found: 0,
        notFound: 0,
        invalid: 0,
      };
    }

    if (typeof normalizedContainers[0] !== 'string') {
      return (normalizedContainers as ContainerAdvancedSearchRow[]).reduce<ContainerAdvancedSearchSummary>((acc, row) => {
        acc.total += 1;
        if (row.classification === 'found') {
          acc.found += 1;
        } else if (row.classification === 'not_found') {
          acc.notFound += 1;
        } else {
          acc.invalid += 1;
        }
        return acc;
      }, {
        total: 0,
        found: 0,
        notFound: 0,
        invalid: 0,
      });
    }

    const normalizedList = normalizedContainers as string[];
    const foundSet = new Set(
      matches
        .map(match => (match.containerNumber || '').trim().toUpperCase())
        .filter(Boolean)
    );

    return normalizedList.reduce<ContainerAdvancedSearchSummary>((acc, containerNumber) => {
      acc.total += 1;
      if (!this.isValidIso6346(containerNumber)) {
        acc.invalid += 1;
      } else if (foundSet.has(containerNumber)) {
        acc.found += 1;
      } else {
        acc.notFound += 1;
      }
      return acc;
    }, {
      total: 0,
      found: 0,
      notFound: 0,
      invalid: 0,
    });
  }

  exportToCsv(rows: ContainerAdvancedSearchRow[], filename = 'busqueda-contenedores.csv', section?: ContainerAdvancedSearchSection): void {
    const isContainersSection = section === 'containers';
    const headers = isContainersSection
      ? ['Número contenedor', 'Clasificación', 'Tipo']
      : [
          'Número contenedor',
          'Clasificación',
          'Orden asociada',
          'Estado actual',
          'Último evento/fecha',
        ];

    const csvRows = rows.map(row =>
      isContainersSection
        ? [row.containerNumber, this.getClassificationLabel(row.classification), row.containerTypeCode ?? '-']
        : [
            row.containerNumber,
            this.getClassificationLabel(row.classification),
            row.referenceOrder || '-',
            row.currentStatus || '-',
            row.lastEventDate || '-',
          ]
    );

    const lines = [
      headers.map(value => this.escapeCsvValue(value)).join(','),
      ...csvRows.map(row => row.map(value => this.escapeCsvValue(value)).join(',')),
    ];

    const csvContent = `\uFEFF${lines.join('\n')}`;
    const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  private getClassificationLabel(classification: ContainerAdvancedSearchRow['classification']): string {
    if (classification === 'found') {
      return 'Encontrado';
    }
    if (classification === 'not_found') {
      return 'No encontrado';
    }
    return 'Inválido';
  }

  private escapeCsvValue(value: string): string {
    const escaped = value.replace(/"/g, '""');
    return `"${escaped}"`;
  }
}
